// File: src/components/home/HeroSection.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useShortlist } from "../../context/ShortlistContext";
import "../../styles/home/HeroSection.css";

import heroImage1 from "../../assets/new-image.jpeg";
import heroImage2 from "../../assets/newly-cars.png";
import heroImage3 from "../../assets/mountain-image.png";

// ── ONE button per slide, each goes to a DIFFERENT page ──
const SLIDES = [
  {
    image: heroImage1,
    id: "view",
    button: { label: "🚗 View All Cars",      route: "/buy-cars" },
  },
  {
    image: heroImage2,
    id: "sell",
    button: { label: "💰 Sell Your Car",       route: "/sell-car" },
  },
  {
    image: heroImage3,
    id: "finance",
    button: { label: "✅ Check Eligibility",   route: "/finance"  },
  },
];

const AUTO_PLAY_INTERVAL = 4500;

const HeroSection = () => {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const { shortlist } = useShortlist();

  const [current,     setCurrent]     = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback(
    (index) => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrent((index + SLIDES.length) % SLIDES.length);
      setTimeout(() => setIsAnimating(false), 600);
    },
    [isAnimating]
  );

  useEffect(() => {
    const timer = setInterval(() => goTo(current + 1), AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const slide = SLIDES[current];

  // ── Logged-in cards (shortlisted car + sell request) ──
  const renderLoggedInCards = () => {
    const cards = [];

    if (shortlist && shortlist.length > 0) {
      const latest = shortlist[shortlist.length - 1];
      cards.push(
        <div key="shortlist" className="hero-action-card" onClick={() => navigate("/shortlisted")}>
          <div className="hac-icon">❤️</div>
          <div className="hac-body">
            <span className="hac-label">You shortlisted</span>
            <span className="hac-value">
              {latest.year} {latest.make} {latest.model || latest.name}
            </span>
          </div>
          {latest.image && <img src={latest.image} alt="car" className="hac-car-img" />}
          <span className="hac-arrow">›</span>
        </div>
      );
    }

    const sellRequest =
      typeof window !== "undefined"
        ? JSON.parse(localStorage.getItem("latestSellRequest") || "null")
        : null;

    if (sellRequest) {
      cards.push(
        <div key="sell" className="hero-action-card" onClick={() => navigate("/my-sells")}>
          <div className="hac-icon">🏷️</div>
          <div className="hac-body">
            <span className="hac-label">Book Evaluation</span>
            <span className="hac-value">
              Schedule a free evaluation for your {sellRequest.make} {sellRequest.model}
            </span>
          </div>
          <button
            className="hac-cta"
            onClick={(e) => { e.stopPropagation(); navigate("/my-sells"); }}
          >
            Book Now
          </button>
        </div>
      );
    }

    return cards;
  };

  return (
    <section className="hero-section">

      {/* ── Background slides ── */}
      {SLIDES.map((s, i) => (
        <div
          key={s.id}
          className={`hero-slide ${i === current ? "active" : ""}`}
          style={{ backgroundImage: `url(${s.image})` }}
          aria-hidden={i !== current}
        />
      ))}

      {/* ── Dark gradient overlay ── */}
      <div className="hero-overlay" />

      {/* ── Foreground content ── */}
      <div className="hero-content">

        {user ? (
          /* LOGGED IN: action cards + current slide button */
          <div className="hero-logged-in">
            {renderLoggedInCards().length > 0 && (
              <div className="hero-action-cards">{renderLoggedInCards()}</div>
            )}
            {/* Current slide's single button */}
            <button
              className="hero-slide-btn"
              onClick={() => navigate(slide.button.route)}
            >
              {slide.button.label}
            </button>
          </div>
        ) : (
          /* LOGGED OUT: only the ONE button for the current slide */
          <div className="hero-logged-out">
            <button
              className="hero-slide-btn"
              onClick={() => navigate(slide.button.route)}
            >
              {slide.button.label}
            </button>
          </div>
        )}

      </div>

      {/* ── Left / Right arrows ── */}
      <button className="hero-arrow left"  onClick={() => goTo(current - 1)} aria-label="Previous">‹</button>
      <button className="hero-arrow right" onClick={() => goTo(current + 1)} aria-label="Next">›</button>

      {/* ── Dot indicators ── */}
      <div className="hero-dots">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            className={`hero-dot ${i === current ? "active" : ""}`}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

    </section>
  );
};

export default HeroSection;